import { useRef, useState } from 'react';
import {
  ApiError,
  Badge,
  Button,
  Card,
  ErrorNote,
  Field,
  Input,
  Page,
  VehicleForm,
  api,
  cx,
  dateOnly,
} from '@mobi/web';
import type { DriverStatus } from '../useDriver.js';

const DOCS = [
  { kind: 'cnh', label: 'CNH', hint: 'Foto da habilitação, frente e verso na mesma imagem' },
  { kind: 'selfie', label: 'Selfie', hint: 'Seu rosto, sem óculos escuros ou boné' },
  { kind: 'crlv', label: 'CRLV', hint: 'Documento do veículo' },
] as const;

export function Onboarding({
  status,
  onChanged,
}: {
  status: DriverStatus;
  onChanged: () => void;
}) {
  const [error, setError] = useState<string | null>(null);

  const needsProfile = !status.hasProfile;
  const needsVehicle = status.hasProfile && !status.vehicle;
  const docsDone =
    status.hasProfile && status.vehicle && !status.missing.some((m) => m !== 'veículo');

  return (
    <Page
      title="Seu cadastro"
      subtitle="Complete os três passos. Depois disso, um administrador confere seus documentos."
    >
      <div className="space-y-4">

        <StatusBanner status={status} />

        {error && <ErrorNote>{error}</ErrorNote>}

        <Step
          number={1}
          title="Seus dados"
          done={status.hasProfile}
          open={needsProfile}
          summary={status.hasProfile ? `CNH válida até ${dateOnly(status.cnhExpiresOn)}` : undefined}
        >
          <ProfileForm onDone={onChanged} onError={setError} />
        </Step>

        <Step
          number={2}
          title="Seu veículo"
          done={Boolean(status.vehicle)}
          open={needsVehicle}
          summary={
            status.vehicle
              ? `${status.vehicle.model} ${status.vehicle.color} · ${status.vehicle.plate}`
              : undefined
          }
          blocked={!status.hasProfile}
        >
          <VehicleForm submitLabel="Salvar veículo" onDone={onChanged} onError={setError} />
        </Step>

        <Step
          number={3}
          title="Documentos"
          done={Boolean(docsDone)}
          open={Boolean(status.vehicle) && !docsDone}
          blocked={!status.vehicle}
          summary={docsDone ? 'Todos enviados' : undefined}
        >
          <div className="space-y-3">
            {DOCS.map((doc) => (
              <DocUpload
                key={doc.kind}
                kind={doc.kind}
                label={doc.label}
                hint={doc.hint}
                sent={!status.missing.includes(doc.label)}
                onDone={onChanged}
                onError={setError}
              />
            ))}
          </div>
        </Step>
      </div>
    </Page>
  );
}

function StatusBanner({ status }: { status: DriverStatus }) {
  if (status.approvalStatus === 'rejected') {
    return (
      <Card tone="danger">
        <p className="font-semibold text-danger">Cadastro recusado</p>
        <p className="mt-1 text-sm text-muted">{status.rejectionReason}</p>
        <p className="mt-2 text-sm text-muted">
          Corrija o que foi apontado e reenvie — seu cadastro volta para a fila automaticamente.
        </p>
      </Card>
    );
  }

  if (status.approvalStatus === 'suspended') {
    return (
      <Card tone="danger">
        <p className="font-semibold text-danger">Cadastro suspenso</p>
        <p className="mt-1 text-sm text-muted">{status.rejectionReason}</p>
        <p className="mt-2 text-sm text-muted">
          Fale com a administração. Editar o cadastro não remove a suspensão.
        </p>
      </Card>
    );
  }

  if (status.readyForReview) {
    return (
      <Card tone="brandSoft">
        <p className="font-semibold">Tudo enviado</p>
        <p className="mt-1 text-sm text-muted">
          Seu cadastro está na fila de aprovação. Você recebe acesso assim que for aprovado.
        </p>
      </Card>
    );
  }

  return null;
}

function Step({
  number,
  title,
  done,
  open,
  blocked,
  summary,
  children,
}: {
  number: number;
  title: string;
  done: boolean;
  open: boolean;
  blocked?: boolean;
  summary?: string;
  children: React.ReactNode;
}) {
  const [expanded, setExpanded] = useState(open);
  const show = expanded || open;

  return (
    <Card className={cx(blocked && 'opacity-50')}>
      <button
        type="button"
        disabled={blocked}
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-3 text-left"
      >
        <span className="flex items-center gap-3">
          <span
            className={cx(
              'grid h-8 w-8 shrink-0 place-items-center rounded-full text-sm font-bold',
              done ? 'bg-good text-white' : 'bg-surface-2 text-muted',
            )}
          >
            {done ? '✓' : number}
          </span>
          <span>
            <span className="block font-semibold">{title}</span>
            {summary && <span className="block text-sm text-muted">{summary}</span>}
          </span>
        </span>
        {done && !show && <Badge tone="good">pronto</Badge>}
      </button>

      {show && !blocked && <div className="mt-5">{children}</div>}
    </Card>
  );
}

function ProfileForm({ onDone, onError }: { onDone: () => void; onError: (m: string) => void }) {
  const [cpf, setCpf] = useState('');
  const [cnhNumber, setCnh] = useState('');
  const [cnhExpiresOn, setExpiry] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await api('/api/driver/profile', { body: { cpf, cnhNumber, cnhExpiresOn } });
      onDone();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'não foi possível salvar');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <Field label="CPF">
        <Input
          value={cpf}
          onChange={(e) => setCpf(e.target.value)}
          inputMode="numeric"
          required
          placeholder="000.000.000-00"
        />
      </Field>
      <Field label="Número da CNH">
        <Input
          value={cnhNumber}
          onChange={(e) => setCnh(e.target.value)}
          inputMode="numeric"
          required
          placeholder="00000000000"
        />
      </Field>
      <Field label="Validade da CNH" hint="CNH vencida não é aceita">
        {/* Campo de data nativo: sem biblioteca de calendário. */}
        <Input
          type="date"
          value={cnhExpiresOn}
          onChange={(e) => setExpiry(e.target.value)}
          required
        />
      </Field>
      <Button type="submit" full loading={busy}>
        Salvar dados
      </Button>
    </form>
  );
}

function DocUpload({
  kind,
  label,
  hint,
  sent,
  onDone,
  onError,
}: {
  kind: string;
  label: string;
  hint: string;
  sent: boolean;
  onDone: () => void;
  onError: (m: string) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  async function upload(file: File) {
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await api(`/api/driver/documents/${kind}`, { formData: form });
      onDone();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'não foi possível enviar o arquivo');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl bg-surface-2 p-4">
      <div className="min-w-0">
        <p className="font-medium">
          {label} {sent && <span className="text-good">✓</span>}
        </p>
        <p className="text-sm text-faint">{hint}</p>
      </div>
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void upload(file);
          e.target.value = '';
        }}
      />
      <Button
        variant="outline"
        loading={busy}
        onClick={() => input.current?.click()}
        className="shrink-0"
      >
        {sent ? 'Trocar' : 'Enviar'}
      </Button>
    </div>
  );
}
